// Gore, falls and shake (the polish pass, 2026-10-15). Matt: "transparency, motion, damage/sprite actions/death
// scenes are all functioning in a similar fashion [to Diablo and Project Zomboid]". All code-driven, no new art:
//   spray    a hit throws droplets that arc and land; each one that lands leaves a pixel on the floor (Zomboid: the
//            blood stays where the fight was)
//   pool     a body that goes down bleeds a slowly widening pool under it
//   gibs     an overkill (a crit that kills) throws a few dark chunks as well
//   fall     the figure tips over away from the hit (Diablo's death animation, done by rotating the frame about the
//            feet with a short bounce), then lies there as a CORPSE for a minute before it fades
//   flinch   a hit leans the figure back and shudders it a pixel (Diablo's get-hit) for the flash's length
//   shake    a hit on YOU or a big crit nudges the camera a few pixels
// Coordinates are the active renderer's screen space (toScreen), so decals live in IsoRenderer.decals (above the
// ground, under everything that stands) and airborne bits in one Graphics sorted on top.
import { Container, Graphics } from 'pixi.js';

export const BLOOD = [0x7a1010, 0x5e0b0b, 0x8f1a14, 0x6b0e0e];
export const OOZE = [0x4b1f16, 0x3a1710, 0x5c2a1a, 0x2e2a12];      // the turned bleed dark and old

export const FALL_MS = 420;             // tip over
export const CORPSE_MS = 60_000;        // lie there
export const CORPSE_FADE_MS = 4_000;    // then fade
const MAX_CORPSES = 40;
const MAX_DECALS = 900;
const DECAL_LIFE_MS = 180_000;          // a stain lasts three minutes, then fades over the last 20 s

interface Drop { x: number; y: number; z: number; vx: number; vy: number; vz: number; c: number; s: number; chunk: boolean; spark?: boolean }
interface Decal { g: Graphics; born: number }
interface Pool { g: Graphics; born: number; r: number; c: number }
export interface Corpse { spr: Container; x: number; y: number; until: number; born: number; sx: number; sy: number }

export class Gore {
  air = new Graphics();
  drops: Drop[] = [];
  decals: Decal[] = [];
  pools: Pool[] = [];
  corpses: Corpse[] = [];
  private shakeA = 0;
  private landed: Graphics | null = null;

  /** one per renderer: the street's lives all session (its stains and bodies are still there when you come back
   *  out); a room's or a dungeon's goes with it. layer: the renderer's decals, sorted: its depth-sorted actors. */
  constructor(public layer: Container, sorted: Container) { this.air.zIndex = 1e9 - 1; sorted.addChild(this.air); }

  /** a hit at screen (sx, sy) on the ground, h px up the body; dx, dy: the hit's screen direction */
  spray(sx: number, sy: number, h: number, dx: number, dy: number, n: number, pal = BLOOD, power = 1) {
    const d = Math.hypot(dx, dy) || 1; dx /= d; dy /= d;
    for (let i = 0; i < n; i++) {
      const sp = (30 + Math.random() * 70) * power, spread = (Math.random() - 0.5) * 1.6;
      const ax = dx * Math.cos(spread) - dy * Math.sin(spread), ay = dx * Math.sin(spread) + dy * Math.cos(spread);
      this.drops.push({ x: sx, y: sy, z: h * (0.6 + Math.random() * 0.4), vx: ax * sp, vy: ay * sp * 0.5, vz: 20 + Math.random() * 60 * power,
        c: pal[(Math.random() * pal.length) | 0], s: Math.random() < 0.25 ? 2 : 1, chunk: false });
    }
  }

  /** an overkill: dark chunks that bounce once */
  gibs(sx: number, sy: number, h: number, dx: number, dy: number, pal = BLOOD) {
    const d = Math.hypot(dx, dy) || 1;
    for (let i = 0; i < 6; i++) {
      const a = Math.atan2(dy / d, dx / d) + (Math.random() - 0.5) * 2.2, sp = 40 + Math.random() * 70;
      this.drops.push({ x: sx, y: sy, z: h * 0.7, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.5, vz: 50 + Math.random() * 60, c: pal[1], s: 2 + (Math.random() < 0.4 ? 1 : 0), chunk: true });
    }
    this.spray(sx, sy, h, dx, dy, 16, pal, 1.4);
  }

  /** bits of whatever broke: glass shards glint, wood splinters, brick dust (they land and stay like the blood) */
  debris(sx: number, sy: number, h: number, cls: string, n: number) {
    const pal = cls === 'glass' ? [0xcfe8f0, 0x9cc4c6, 0xffffff] : cls === 'wood' ? [0x6b4a2a, 0x8a6236, 0x4a3018] : cls === 'metal' ? [0x8a8f96, 0x5a5e66, 0xffd38a] : [0x8a4a3a, 0xa9a29a, 0x6e6660];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, sp = 20 + Math.random() * 60;
      this.drops.push({ x: sx, y: sy, z: h * Math.random(), vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.5 + 10, vz: 10 + Math.random() * 50, c: pal[(Math.random() * pal.length) | 0], s: cls === 'glass' ? 1 : 1 + (Math.random() < 0.3 ? 1 : 0), chunk: cls !== 'glass' && Math.random() < 0.3 });
    }
  }

  /** a spell or bolt landing: bright sparks that burn out instead of staining */
  sparks(sx: number, sy: number, h: number, col: number, n: number) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, sp = 30 + Math.random() * 80;
      this.drops.push({ x: sx, y: sy, z: h, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.5, vz: 20 + Math.random() * 70, c: Math.random() < 0.4 ? 0xffffff : col, s: 1, chunk: false, spark: true });
    }
  }

  /** a body down: a pool that spreads under it */
  pool(sx: number, sy: number, r: number, pal = BLOOD) {
    const g = new Graphics(); g.x = Math.round(sx); g.y = Math.round(sy); this.layer.addChild(g);
    this.pools.push({ g, born: performance.now(), r, c: pal[1] });
  }

  reduce = false;                          // OPTIONS: reduced motion - no camera shake
  shake(px: number) { if (!this.reduce) this.shakeA = Math.max(this.shakeA, px); }

  addCorpse(spr: Container, x: number, y: number) {
    // the body greys as it settles; after a while the flies find it (Zomboid)
    const b = spr.children[0] as any; if (b) b.tint = 0x8f8780;
    this.corpses.push({ spr, x, y, until: performance.now() + CORPSE_MS, born: performance.now(), sx: spr.x, sy: spr.y });
    while (this.corpses.length > MAX_CORPSES) this.corpses.shift()!.spr.destroy({ children: true });
  }

  /** step everything; returns the camera shake offset for this frame */
  update(dt: number): [number, number] {
    const now = performance.now();
    const g = this.air; g.clear();
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const p = this.drops[i];
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt; p.vz -= 260 * dt;
      if (p.z <= 0) {
        if (p.chunk && p.vz < -40) { p.z = 0; p.vz = -p.vz * 0.3; p.vx *= 0.5; p.vy *= 0.5; continue; }
        if (!p.spark) this.stamp(p);
        this.drops.splice(i, 1); continue;
      }
      g.rect(Math.round(p.x), Math.round(p.y - p.z), p.s, p.s).fill({ color: p.c });
    }
    this.landed = null;
    for (let i = this.pools.length - 1; i >= 0; i--) {
      const P = this.pools[i]; const k = Math.min(1, (now - P.born) / 1600);
      if (k >= 1 && (P.g as any).done) continue;
      const e = 1 - Math.pow(1 - k, 3);
      P.g.clear().ellipse(0, 0, P.r * e, P.r * e * 0.5).fill({ color: P.c, alpha: 0.85 }).ellipse(-P.r * 0.25 * e, -P.r * 0.08 * e, P.r * 0.45 * e, P.r * 0.2 * e).fill({ color: 0x9a2a22, alpha: 0.35 });
      if (k >= 1) { (P.g as any).done = true; this.decals.push({ g: P.g, born: P.born }); this.pools.splice(i, 1); }
    }
    // stains age out; the oldest go first when there are too many
    while (this.decals.length > MAX_DECALS) this.decals.shift()!.g.destroy();
    for (let i = this.decals.length - 1; i >= 0; i--) {
      const D = this.decals[i], age = now - D.born;
      if (age > DECAL_LIFE_MS) { D.g.destroy(); this.decals.splice(i, 1); }
      else if (age > DECAL_LIFE_MS - 20_000) D.g.alpha = (DECAL_LIFE_MS - age) / 20_000;
    }
    for (let i = this.corpses.length - 1; i >= 0; i--) {
      const C = this.corpses[i], left = C.until - now;
      if (left <= 0) { C.spr.destroy({ children: true }); this.corpses.splice(i, 1); }
      else if (left < CORPSE_FADE_MS) C.spr.alpha = left / CORPSE_FADE_MS;
      if (now - C.born > 15_000 && left > CORPSE_FADE_MS) for (let f = 0; f < 3; f++) {
        const a = now / (180 + f * 40) + f * 2.1;
        g.rect(Math.round(C.sx + Math.cos(a) * (5 + f * 2)), Math.round(C.sy - 8 + Math.sin(a * 1.7) * 3 - f), 1, 1).fill({ color: 0x111111 });
      }
    }
    if (this.shakeA < 0.3) { this.shakeA = 0; return [0, 0]; }
    const s = this.shakeA; this.shakeA *= Math.pow(0.0008, dt);           // gone in about 150 ms
    return [Math.round((Math.random() * 2 - 1) * s), Math.round((Math.random() * 2 - 1) * s * 0.6)];
  }

  private stamp(p: Drop) {
    if (!this.landed) { this.landed = new Graphics(); this.layer.addChild(this.landed); this.decals.push({ g: this.landed, born: performance.now() }); }
    const x = Math.round(p.x), y = Math.round(p.y);
    this.landed.rect(x, y, p.s + (p.chunk ? 1 : 0), p.s).fill({ color: p.c, alpha: p.chunk ? 1 : 0.9 });
    if (!p.chunk && Math.random() < 0.3) this.landed.rect(x + 1, y, 1, 1).fill({ color: p.c, alpha: 0.6 });   // a short smear
  }
}

/** the fall: rotation (radians) and a squash for a body that died `ms` ago, tipping toward `side` (-1 left, 1 right) */
export function fallPose(ms: number, side: number): { rot: number; sy: number; dy: number } {
  const k = Math.min(1, ms / FALL_MS);
  const e = k * k;                                         // accelerates like a falling body
  const bounce = k >= 1 ? 0 : Math.max(0, Math.sin((k - 0.82) / 0.18 * Math.PI)) * (k > 0.82 ? 0.08 : 0);
  return { rot: side * (Math.PI / 2) * (e - bounce), sy: 1 - 0.28 * e, dy: Math.round(2 * e) };
}

/** the get-hit lean: a short tilt away from the hit and a 1 px shudder while the flash lasts */
export function flinch(ms: number, side: number): { rot: number; dx: number } {
  if (ms > 160) return { rot: 0, dx: 0 };
  const k = 1 - ms / 160;
  return { rot: side * 0.16 * k, dx: ms < 90 ? (Math.floor(ms / 30) % 2 ? 1 : -1) : 0 };
}
